import type { CloudApi } from "../../cloud/api.ts";
import type { MemberNotification, WebMemberNotifications } from "../../cloud/member-notifications.ts";
import {
	PlanSwitchBusyError,
	planLogin,
	type WebMemberConnection,
	type WebNativeSubscription,
} from "../../cloud/native-subscription.ts";
import type { WebRoutine, WebRoutines } from "../../cloud/routines.ts";
import { button, h } from "../../ui/dom.ts";
import { matchesKey } from "../../ui/keys.ts";
import { t } from "../../ui/preferences.ts";

type Client = Pick<WebMemberNotifications, "listNotifications" | "acknowledgeNotification">;
/** A worker's own run, which has no conversation: the notice line offers its Stop (gap 4b). */
export type WorkerRuns = Pick<CloudApi, "workerRunControl" | "stopWorkerRun">;
/** What the band needs to ask a ChatGPT plan's member to turn on work while away (Sign in with ChatGPT). */
export interface PlanOffer {
	native: Pick<WebNativeSubscription, "listConnections"> &
		Required<Pick<WebNativeSubscription, "setWorkWhileAway" | "recordRoutineOffer" | "chatGptPlanAvailable">>;
	routines: Pick<WebRoutines, "list" | "setEnabled">;
	/** The signed-in member: the card restarts only routines that run for them. */
	memberId: string;
	/** Owners and admins save routines; for anyone else the card turns the plan on and says what is next. */
	canManage: () => boolean;
}
/** The card while it stands: the notices that raised it, the member's waiting routines, the plan. */
interface Offer {
	notices: readonly Waiting[];
	routines: readonly WebRoutine[];
	plan: WebMemberConnection;
}
/** A routine of this member that waits for a payer a plan can be: never a file-change or webhook routine. */
const waitsForPlan = (routine: WebRoutine, memberId: string) =>
	routine.status === "paused" &&
	routine.pauseReason === "needs_model_account" &&
	routine.runsForUserId === memberId &&
	!routine.triggers.some((trigger) => trigger.kind === "listener");
type Waiting = MemberNotification & {
	subject: { schema: "routine-needs-model-account"; routineId: string; title: string };
};
const waiting = (item: MemberNotification): item is Waiting => item.subject.schema === "routine-needs-model-account";
/** The member's ChatGPT plan that may be offered: signed in, not turned on, its card not shown
 * before (or shown, and not answered, in this window). Undefined for every member without a plan login. */
function offerable(
	connections: readonly WebMemberConnection[],
	offered: ReadonlySet<string>,
	answered: ReadonlySet<string>,
) {
	const newest = new Map<string, WebMemberConnection>();
	for (const item of connections) {
		const current = newest.get(item.connectionId);
		if (!current || current.connectionVersion < item.connectionVersion) newest.set(item.connectionId, item);
	}
	return [...newest.values()].find(
		(item) =>
			planLogin(item) &&
			item.connectionStatus === "active" &&
			item.authorizationState === "active" &&
			item.allowAutonomous !== true &&
			!answered.has(item.connectionId) &&
			(!item.routineOfferShownAt || offered.has(item.connectionId)),
	);
}

/** What a notice tells the member, in plain words; undefined when it gets no line.
 * A reached usage limit is told by the failed run's card in the thread. */
function sentence(
	item: MemberNotification,
	agentName?: (staffAgentId: string) => string | undefined,
	memberName?: (userId: string) => string | undefined,
): string | undefined {
	const subject = item.subject;
	switch (subject.schema) {
		case "qorpera.member-notification.routine-payer-changed/v1": {
			// Someone else's save or pick moved a routine off this member's login (20260930095000).
			const agent = agentName?.(subject.staffAgentId) ?? t("An agent");
			const member = (subject.changedByUserId && memberName?.(subject.changedByUserId)) || t("A member");
			if (subject.payer === "company")
				return t("{agent}'s routine no longer runs on your login. {member} moved it to the company's account.", {
					agent,
					member,
				});
			return subject.payerUserId === subject.changedByUserId
				? t("{agent}'s routine no longer runs on your login. {member} moved it to their login.", { agent, member })
				: t("{agent}'s routine no longer runs on your login. {member} moved it to {payer}'s login.", {
						agent,
						member,
						payer: (subject.payerUserId && memberName?.(subject.payerUserId)) || t("another member"),
					});
		}
		case "qorpera.member-notification.interface-supervision-ended/v1": {
			// An area's supervisor no longer supervises its agents, who act for no one until someone does (20260930371000).
			const member = memberName?.(subject.supervisorUserId) || t("A member");
			const name = subject.key
				?.map((value) => (Array.isArray(value) ? value.join(", ") : value === null ? "" : String(value)))
				.filter((value) => value !== "")
				.join(", ");
			const area = name ? t("the area {name}", { name }) : t("an area");
			if (subject.reason === "withdrawn")
				return t("{member} stopped supervising the agents in {area}.", { member, area });
			if (subject.reason === "released")
				return t("{member} no longer supervises the agents in {area}.", { member, area });
			if (subject.reason === "gone")
				return t("{member} no longer supervises the agents in {area}: the area is gone.", { member, area });
			return t("{member} left the company, so the agents in {area} have no supervisor.", { member, area });
		}
		case "qorpera.member-notification.run-no-progress/v1":
			return t("{agent} has made no progress for {minutes} minutes.", {
				agent: agentName?.(subject.staffAgentId) ?? t("An agent"),
				minutes: subject.minutes,
			});
		case "qorpera.member-notification.provider-usage-limit/v1":
			return undefined;
		case "qorpera.member-notification.machine-capacity/v1":
			return t("All {count} company computers are in use. Close one to open another.", { count: subject.ceiling });
		case "routine-needs-model-account":
			// Only a ChatGPT plan's member is asked, once, by the plan card below.
			return undefined;
	}
}

/** The unacknowledged notices that get a line. */
function told(items: readonly MemberNotification[]): MemberNotification[] {
	return items.filter((item) => !item.acknowledgedAt && sentence(item) !== undefined);
}

/** Member-addressed runtime notices are one quiet line above the composer, never a
 * popup, and stay until explicitly dismissed. Polling never acknowledges, replays a
 * write or steals focus. A failed read or an unconfirmed dismissal shows nothing: the
 * line returns with the next good read if the notice still stands. */
export class ExecutionNotices {
	readonly el = h("section", { class: "execution-notices", "aria-label": t("Company updates"), hidden: true });
	readonly #client: Client;
	readonly #lifetime = new AbortController();
	readonly #restoreFocus: (() => void) | undefined;
	readonly #plan: PlanOffer | undefined;
	readonly #agentName: ((staffAgentId: string) => string | undefined) | undefined;
	readonly #workerRuns: WorkerRuns | undefined;
	/** A member's name, for who moved a routine off this member's login. */
	readonly #memberName: ((userId: string) => string | undefined) | undefined;
	/** Per notice of a worker's run: whether this member may stop it, once read. */
	readonly #stoppable = new Map<string, boolean>();
	#stopping = false;
	/** Notices whose Stop failed: the line says so and keeps the button. */
	readonly #stopFailed = new Set<string>();
	#items: readonly MemberNotification[] = [];
	#offer: Offer | undefined;
	/** Plans whose card this window showed (and recorded): it stays until answered here. */
	readonly #offered = new Set<string>();
	/** Plans answered in this window (Turn on or Not now): their card never comes back. */
	readonly #answered = new Set<string>();
	/** Plans this window turned on: a retry after a failed routine save does not turn them on again. */
	readonly #turnedOn = new Set<string>();
	/** The waiting notices last read with nothing to offer: the next poll reads no more unless they change. */
	#offerChecked = "";
	#offerBusy: "on" | "not-now" | undefined;
	#offerError: string | undefined;
	/** After Turn on, for a member who does not save routines: what to do next. */
	#offerDone: string | undefined;
	readonly #hidden = new Set<string>();
	#request: AbortController | undefined;
	#timer: ReturnType<typeof setInterval> | undefined;
	#acking = false;
	#drawn = "";

	constructor(
		client: Client,
		signal: AbortSignal,
		restoreFocus?: () => void,
		plan?: PlanOffer,
		agentName?: (staffAgentId: string) => string | undefined,
		workerRuns?: WorkerRuns,
		memberName?: (userId: string) => string | undefined,
	) {
		this.#client = client;
		this.#restoreFocus = restoreFocus;
		this.#plan = plan;
		this.#agentName = agentName;
		this.#workerRuns = workerRuns;
		this.#memberName = memberName;
		if (signal.aborted) {
			this.dispose();
			return;
		}
		signal.addEventListener("abort", () => this.dispose(), { once: true, signal: this.#lifetime.signal });
		for (const event of ["focus", "online"])
			window.addEventListener(event, () => void this.refresh(), { signal: this.#lifetime.signal });
		document.addEventListener("visibilitychange", () => void this.refresh(), { signal: this.#lifetime.signal });
		this.#timer = setInterval(() => void this.refresh(), 30_000);
		void this.refresh();
	}

	async refresh(): Promise<void> {
		if (
			this.#lifetime.signal.aborted ||
			this.#request ||
			this.#acking ||
			this.#offerBusy ||
			document.visibilityState === "hidden"
		)
			return;
		const request = new AbortController();
		this.#request = request;
		const signal = AbortSignal.any([request.signal, this.#lifetime.signal]);
		try {
			const items = await this.#client.listNotifications(signal);
			if (signal.aborted) return;
			this.#items = told(items);
			await this.#readStop(signal);
			// The plan card has its own reads; a failed one hides only the card, and the next poll reads again.
			if (!this.#offerDone)
				this.#offer = await this.#readOffer(items, signal).catch((error: unknown) => {
					if (signal.aborted) throw error;
					this.#offerChecked = "";
					return undefined;
				});
		} catch {
			if (signal.aborted) return;
			// Nothing stale is shown, and nothing can be dismissed, until a read succeeds. A card shown
			// before comes back then: its plan is still offered in this window.
			this.#items = [];
			this.#offer = undefined;
			this.#offerChecked = "";
		} finally {
			if (this.#request === request) this.#request = undefined;
			if (!signal.aborted) this.#draw();
		}
	}

	/** An agent's routine waits for this member's account, and the member has a ChatGPT plan they have not
	 * turned on, while plans are on: the card asks them once. Members without a plan login never get here. */
	async #readOffer(items: readonly MemberNotification[], signal: AbortSignal): Promise<Offer | undefined> {
		const plan = this.#plan;
		const notices = items.filter(
			(item): item is Waiting => !item.acknowledgedAt && waiting(item) && !this.#hidden.has(item.notificationId),
		);
		if (!plan || !notices.length) return undefined;
		const key = notices
			.map((item) => item.notificationId)
			.sort()
			.join();
		if (this.#offer && key === this.#offerChecked) return this.#offer;
		if (key === this.#offerChecked) return undefined;
		this.#offerChecked = key;
		if (!(await plan.native.chatGptPlanAvailable(signal))) return undefined;
		const connection = offerable(
			(await plan.native.listConnections(signal)).connections,
			this.#offered,
			this.#answered,
		);
		if (!connection) return undefined;
		const routines = (await plan.routines.list()).filter((routine) => waitsForPlan(routine, plan.memberId));
		return routines.length ? { notices, routines, plan: connection } : undefined;
	}

	/** Turns on work while away for the plan, then (for an owner or admin) saves each waiting routine on it
	 * again; anyone else is told to ask the agent. */
	async #turnOn(): Promise<void> {
		const offer = this.#offer;
		const plan = this.#plan;
		if (this.#lifetime.signal.aborted || this.#acking || this.#offerBusy || !offer || !plan) return;
		const signal = this.#lifetime.signal;
		const id = offer.plan.connectionId;
		this.#request?.abort();
		this.#request = undefined;
		this.#offerBusy = "on";
		this.#offerError = undefined;
		this.#draw();
		// What still waits after the switch (undefined until it is read), and how many of those were started.
		let still: WebRoutine[] | undefined;
		let started = 0;
		try {
			if (!this.#turnedOn.has(id)) {
				await plan.native.setWorkWhileAway(id, true, signal);
				this.#turnedOn.add(id);
			}
			this.#answered.add(id);
			// Turning the plan on starts the member's routines that waited only for it (20260929985000). Read again
			// what still waits: one the kernel left (busy at that moment, or one it does not start, such as one whose
			// payer changed), or every one on a kernel without that step. The card never says a routine started that
			// did not.
			still = (await plan.routines.list()).filter((entry) => waitsForPlan(entry, plan.memberId));
			if (still.length && plan.canManage()) {
				const account = (await plan.native.listConnections(signal)).connections
					.filter((item) => item.connectionId === id && item.allowAutonomous === true)
					.sort((a, b) => b.connectionVersion - a.connectionVersion)[0];
				if (!account) throw new Error("The plan did not turn on");
				// One the agent paused, concluded or removed meanwhile is not in the list read above.
				for (const routine of still) {
					await plan.routines.setEnabled(routine, account, true, crypto.randomUUID());
					started += 1;
				}
			} else if (still.length)
				this.#offerDone =
					still.length === 1
						? t("Your ChatGPT plan now works while you're away. Ask the agent to set {title} up again.", {
								title: still[0]!.title,
							})
						: new Set(still.map((routine) => routine.staffAgentId)).size > 1
							? t(
									"Your ChatGPT plan now works while you're away. Ask the agents to set those routines up again.",
								)
							: t(
									"Your ChatGPT plan now works while you're away. Ask the agent to set those routines up again.",
								);
			for (const item of offer.notices) {
				await this.#client.acknowledgeNotification(item.notificationId, signal).catch(() => undefined);
				this.#hidden.add(item.notificationId);
			}
			if (!this.#offerDone) this.#offer = undefined;
		} catch (error) {
			if (!signal.aborted)
				// Only what is known: the switch failed, the routines could not be read again, or these could not start.
				this.#offerError = !this.#turnedOn.has(id)
					? error instanceof PlanSwitchBusyError
						? t("Busy right now. Try again in a moment.")
						: t("“Work while you're away” could not be turned on. Try again.")
					: !still
						? t("“Work while you're away” is on, but your routines could not be checked. Try again.")
						: still.length - started > 1
							? t("“Work while you're away” is on, but the routines could not be started. Try again.")
							: t("“Work while you're away” is on, but the routine could not be started. Try again.");
		} finally {
			this.#offerBusy = undefined;
			if (!signal.aborted) this.#draw();
		}
	}

	/** "Not now": the routines keep waiting, and the card never comes back. */
	async #notNow(): Promise<void> {
		const offer = this.#offer;
		if (this.#lifetime.signal.aborted || this.#acking || this.#offerBusy || !offer) return;
		this.#request?.abort();
		this.#request = undefined;
		this.#offerBusy = "not-now";
		this.#answered.add(offer.plan.connectionId);
		this.#draw();
		for (const item of offer.notices) {
			await this.#client.acknowledgeNotification(item.notificationId, this.#lifetime.signal).catch(() => undefined);
			this.#hidden.add(item.notificationId);
		}
		this.#offerBusy = undefined;
		if (!this.#lifetime.signal.aborted) {
			this.#offer = undefined;
			this.#draw();
		}
	}

	/** Closes the line that says what to do next. */
	#closeDone(): void {
		this.#offerDone = undefined;
		this.#offer = undefined;
		this.#draw();
	}

	/** Dismisses the shown notice, or the one a finished Stop names; nothing else while a Stop runs. */
	async #acknowledge(stopped?: MemberNotification): Promise<void> {
		if (this.#lifetime.signal.aborted || this.#acking || (this.#stopping && !stopped)) return;
		const item = stopped ?? this.#items.find((entry) => !this.#hidden.has(entry.notificationId));
		if (!item) return;
		this.#request?.abort();
		this.#request = undefined;
		this.#acking = true;
		this.#draw();
		try {
			const receipt = await this.#client.acknowledgeNotification(item.notificationId, this.#lifetime.signal);
			if (this.#lifetime.signal.aborted) return;
			if (
				receipt.notificationId !== item.notificationId ||
				receipt.profileId !== item.profileId ||
				!receipt.acknowledgedAt
			)
				throw new Error("Notification receipt mismatch");
		} catch {
			// The write is never replayed. The line stays closed in this window either way.
		} finally {
			if (!this.#lifetime.signal.aborted) {
				this.#hidden.add(item.notificationId);
				this.#acking = false;
				this.#draw();
			}
		}
	}

	/** A shown notice of a worker's run asks once whether this member may stop it; a failed read offers nothing. */
	async #readStop(signal: AbortSignal): Promise<void> {
		const item = this.#items.find((entry) => !this.#hidden.has(entry.notificationId));
		const subject = item?.subject;
		if (!item || !this.#workerRuns || subject?.schema !== "qorpera.member-notification.run-no-progress/v1") return;
		if (!subject.worker || this.#stoppable.has(item.notificationId)) return;
		const control = await this.#workerRuns.workerRunControl(subject.runId).catch(() => undefined);
		// A failed read offers nothing now and is asked again at the next poll.
		if (signal.aborted || !control) return;
		this.#stoppable.set(item.notificationId, control.canCancel);
	}

	/** Stops the worker's run the shown notice names. Once stopped (or already ended), the notice is done. */
	async #stop(): Promise<void> {
		const item = this.#items.find((entry) => !this.#hidden.has(entry.notificationId));
		const subject = item?.subject;
		if (
			this.#lifetime.signal.aborted ||
			this.#stopping ||
			this.#acking ||
			!item ||
			!this.#workerRuns ||
			subject?.schema !== "qorpera.member-notification.run-no-progress/v1"
		)
			return;
		this.#stopping = true;
		this.#stopFailed.delete(item.notificationId);
		this.#draw();
		let ended = false;
		try {
			const control = await this.#workerRuns.stopWorkerRun(subject.runId);
			ended = control.status !== "queued" && control.status !== "leased";
		} catch {
			// Never replayed; the member may press Stop again.
		}
		if (this.#lifetime.signal.aborted) return;
		if (ended) {
			// The stopped notice itself, even if another one shows meanwhile.
			await this.#acknowledge(item);
			this.#stopping = false;
			this.#draw();
		} else {
			this.#stopping = false;
			this.#stopFailed.add(item.notificationId);
			this.#draw();
		}
	}

	#draw(): void {
		if (this.#offer) {
			this.#drawOffer(this.#offer);
			return;
		}
		const item = this.#items.find((entry) => !this.#hidden.has(entry.notificationId));
		const text = item && sentence(item, this.#agentName, this.#memberName);
		const stoppable = item ? this.#stoppable.get(item.notificationId) === true : false;
		const stopFailed = item ? this.#stopFailed.has(item.notificationId) : false;
		const key = JSON.stringify([item?.notificationId, text, this.#acking, stoppable, this.#stopping, stopFailed]);
		if (key === this.#drawn) return;
		const focused = this.el.contains(document.activeElement);
		const focusedAction = focused ? (document.activeElement as HTMLElement).dataset.action : undefined;
		if (text) {
			const dismiss = button(t("Dismiss"), (event) => event.detail < 2 && void this.#acknowledge(), {
				small: true,
			});
			dismiss.dataset.action = "dismiss-notification";
			if (this.#acking || this.#stopping) dismiss.setAttribute("aria-disabled", "true");
			// A worker's run has no conversation: its Stop is here, for a member who may stop it (gap 4b).
			let stop: HTMLElement | null = null;
			if (stoppable) {
				stop = button(t("Stop"), (event) => event.detail < 2 && void this.#stop(), { small: true });
				stop.dataset.action = "stop-worker-run";
				if (this.#stopping || this.#acking) stop.setAttribute("aria-disabled", "true");
			}
			this.el.replaceChildren(
				h(
					"div",
					{
						class: "company-notice",
						role: "status",
						onkeydown: (event: KeyboardEvent) => {
							if (!matchesKey(event, "dismiss")) return;
							event.preventDefault();
							event.stopPropagation();
							void this.#acknowledge();
						},
					},
					h("p", null, text),
					stopFailed
						? h("p", { class: "company-notice-error" }, t("The work could not be stopped. Try again."))
						: null,
					stop,
					dismiss,
				),
			);
		} else this.el.replaceChildren();
		this.el.hidden = !text;
		if (focused) {
			const target =
				(focusedAction && this.el.querySelector<HTMLElement>(`button[data-action="${focusedAction}"]`)) ||
				this.el.querySelector<HTMLElement>("button[data-action]");
			if (target) target.focus({ preventScroll: true });
			else this.#restoreFocus?.();
		}
		this.#drawn = key;
	}

	/** The plan card, in the notice's shape: what waits, and Turn on or Not now. Showing it, on a page
	 * the member can see, records the showing, so it comes once per member. */
	#drawOffer(offer: Offer): void {
		const first = offer.routines[0]!;
		const key = JSON.stringify([
			"plan",
			offer.plan.connectionId,
			offer.routines.map((routine) => routine.routineId),
			this.#offerBusy,
			this.#offerError,
			this.#offerDone,
		]);
		if (
			!this.#offered.has(offer.plan.connectionId) &&
			this.el.isConnected &&
			document.visibilityState === "visible"
		) {
			this.#offered.add(offer.plan.connectionId);
			void this.#plan?.native
				.recordRoutineOffer(offer.plan.connectionId, this.#lifetime.signal)
				.catch(() => undefined);
		}
		if (key === this.#drawn) return;
		const focused = this.el.contains(document.activeElement);
		const card = (text: (Node | string | null)[], ...actions: HTMLElement[]) =>
			h(
				"div",
				{
					class: "company-notice",
					role: "status",
					onkeydown: (event: KeyboardEvent) => {
						if (!matchesKey(event, "dismiss")) return;
						event.preventDefault();
						event.stopPropagation();
						if (this.#offerDone) this.#closeDone();
						else void this.#notNow();
					},
				},
				h("p", null, ...text),
				...actions,
			);
		if (this.#offerDone) {
			const close = button(t("Dismiss"), (event) => event.detail < 2 && this.#closeDone(), { small: true });
			close.dataset.action = "dismiss-plan";
			this.el.replaceChildren(card([this.#offerDone], close));
		} else {
			const turnOn = button(
				this.#offerBusy === "on" ? t("Turning on…") : t("Turn on"),
				(event) => event.detail < 2 && void this.#turnOn(),
				{ small: true, primary: true },
			);
			turnOn.dataset.action = "turn-on-plan";
			const notNow = button(t("Not now"), (event) => event.detail < 2 && void this.#notNow(), { small: true });
			notNow.dataset.action = "not-now-plan";
			if (this.#offerBusy) for (const item of [turnOn, notNow]) item.setAttribute("aria-disabled", "true");
			this.el.replaceChildren(
				card(
					[
						offer.routines.length > 1
							? t("{count} routines can run while you're away.", { count: offer.routines.length })
							: t("{title} can run while you're away.", { title: first.title }),
						" ",
						offer.routines.length > 1
							? t("Turn on “Work while you're away” so your ChatGPT plan can run them.")
							: t("Turn on “Work while you're away” so your ChatGPT plan can run it."),
						this.#offerError ? " " : null,
						this.#offerError ? h("span", { role: "alert" }, this.#offerError) : null,
					],
					turnOn,
					notNow,
				),
			);
		}
		this.el.hidden = false;
		if (focused) this.el.querySelector<HTMLElement>("button[data-action]")?.focus({ preventScroll: true });
		this.#drawn = key;
	}

	dispose(): void {
		this.#lifetime.abort();
		this.#request?.abort();
		clearInterval(this.#timer);
		this.#items = [];
		this.el.replaceChildren();
		this.el.hidden = true;
		this.el.remove();
	}
}
